// MessengerPress — right panel: structured preset editor + submit.
// Built entirely on existing MDL components (Flex, Text, Button, Input,
// Dropdown, FullscreenModal, IconButton, Icon, useSetToast). Replaces
// the previous raw-JSON textarea with a per-operation form rendered from
// `operationsCatalog.ts`. Supports full CRUD against the S3-backed
// preset store: load, edit fields, add/remove/reorder ops, save, save
// as new variant, delete a variant, and promote-to-default (with a
// confirmation modal that warns the admin they're overwriting the
// existing default preset).
//
// Dimensional values are in INCHES — the BE converts to points at run
// time inside the orchestrator.

import { useEffect, useMemo, useState, type ComponentType } from 'react'
import styled from 'styled-components'
import colors from 'utils/styling/colors'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import { Button, Input as RawInput } from '@/components/priority'
import RawIconButton from '@/components/priority/IconButton/IconButton'
import {
  Dropdown as RawDropdown,
  FullscreenModal as RawFullscreenModal,
} from '@/components/global'
import { useSetToast } from '@/hooks/utility'

// MDL JS components (Input, Dropdown, IconButton, FullscreenModal) have
// `forwardRef<any, any>` types in their .js files, so tsc has no idea
// what props they accept. Alias each to a loose ComponentType whose
// props are a plain object — avoids scattering `@ts-expect-error` on
// every JSX attribute without bringing in explicit `any`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseProps = Record<string, any>
const Input = RawInput as unknown as ComponentType<LooseProps>
const IconButton = RawIconButton as unknown as ComponentType<LooseProps>
const Dropdown = RawDropdown as unknown as ComponentType<LooseProps>
const FullscreenModal =
  RawFullscreenModal as unknown as ComponentType<LooseProps>
import {
  createMessengerPressJob,
  deleteMessengerPressPreset,
  getMessengerPressPresets,
  listMessengerPressMasks,
  promoteMessengerPressPresetToDefault,
  putMessengerPressPreset,
  type CreateMessengerPressPayload,
  type MessengerPressJob,
  type MessengerPressOperation,
  type MessengerPressPreset,
} from '@/api/messenger-press'
import {
  OPERATION_DEFS,
  TERMINAL_FLATTEN_OP,
  blankOperation,
  operationDefByType,
  type FieldDef,
} from './operationsCatalog'

// `flatten` is appended automatically as the final step before save / run.
// On load we strip any trailing flatten ops from the editor view so the
// client never sees or edits it. Both functions are pure helpers.
const stripTrailingFlatten = (
  ops: Array<Record<string, unknown> & { type: string }>,
): Array<Record<string, unknown> & { type: string }> => {
  let end = ops.length
  while (end > 0 && ops[end - 1]?.type === 'flatten') end -= 1
  return ops.slice(0, end)
}

const appendFlatten = (
  ops: Array<Record<string, unknown> & { type: string }>,
): Array<Record<string, unknown> & { type: string }> => [
  ...stripTrailingFlatten(ops),
  { ...TERMINAL_FLATTEN_OP },
]

// Inline data-testid constants — kept in lockstep with the central
// registry at apps/mdl-frontend/tests/utils/selectors.ts.
const TID_RECIPE_BUILDER = 'messenger-press-recipe-builder'
const TID_RUN_BUTTON = 'messenger-press-run-button'
const TID_OPERATIONS_LIST = 'messenger-press-recipe-builder-operations-list'
// One per rendered step card, suffixed with the step's 1-based position —
// the same number the card's own "(step N of M)" label shows.
const TID_OPERATION_STEP_PREFIX =
  'messenger-press-recipe-builder-operation-step-'
const TID_ADD_OP_SELECT = 'messenger-press-recipe-builder-add-op'
const TID_PRESET_SELECT = 'messenger-press-recipe-builder-preset'
const TID_PRODUCT_SELECT = 'messenger-press-recipe-builder-product'
const TID_SAVE_BUTTON = 'messenger-press-preset-save'
const TID_SAVE_AS_BUTTON = 'messenger-press-preset-save-as'
const TID_DELETE_BUTTON = 'messenger-press-preset-delete'
const TID_PROMOTE_BUTTON = 'messenger-press-preset-promote-default'

const DEFAULT_VARIANT = 'default'

export type RecipeBuilderProps = {
  selectedPdfId?: string
  // Manual uploads and non-DB URLs flow through this prop. When the id is a
  // `manual-upload:` synthetic and a url is present, the submit payload uses
  // sourcePdfUrl instead of sourcePdfId.
  selectedPdfUrl?: string
  selectedPdfProductId?: string
  // Free-form display label for the source (filename for uploads, product
  // name for picked rows). Shown in the "SOURCE PDF" summary block.
  selectedPdfLabel?: string
  onJobCreated?: (job: MessengerPressJob) => void
}

// Local op shape — every field is unknown so we can edit it generically.
type OpRow = Record<string, unknown> & { type: string }

const variantFromPresetId = (preset: MessengerPressPreset): string => {
  // BE preset ids are `<productId>__<variant>` per presetSchema.ts
  const idx = preset.id.indexOf('__')
  return idx >= 0 ? preset.id.slice(idx + 2) : DEFAULT_VARIANT
}

const MANUAL_UPLOAD_ID_PREFIX = 'manual-upload:'
const isManualUpload = (id: string | undefined): boolean =>
  !!id && id.startsWith(MANUAL_UPLOAD_ID_PREFIX)

export const RecipeBuilder = ({
  selectedPdfId,
  selectedPdfUrl,
  selectedPdfProductId,
  selectedPdfLabel,
  onJobCreated,
}: RecipeBuilderProps) => {
  const setToast = useSetToast()

  const [allPresets, setAllPresets] = useState<MessengerPressPreset[]>([])
  const [presetsLoading, setPresetsLoading] = useState(true)
  const [presetsError, setPresetsError] = useState<string | null>(null)
  const [masks, setMasks] = useState<string[]>([])
  const [pickedVariant, setPickedVariant] = useState<string>(DEFAULT_VARIANT)
  const [activeProductId, setActiveProductId] = useState<string>('')
  const [presetName, setPresetName] = useState<string>('')
  const [operations, setOperations] = useState<OpRow[]>([])
  // Manual theme override — feeds into the submit payload so theme-gated
  // ops (prependPdf / removePages with whenThemeIdIn) can be tested
  // against a manually uploaded source PDF that has no OrderProduct tie.
  const [themeIdOverride, setThemeIdOverride] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [confirmPromote, setConfirmPromote] = useState(false)
  const [saveAsOpen, setSaveAsOpen] = useState(false)
  const [saveAsName, setSaveAsName] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Load all presets + mask file list once on mount.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setPresetsLoading(true)
        setPresetsError(null)
        const [presetsRes, masksRes] = await Promise.all([
          getMessengerPressPresets(),
          listMessengerPressMasks(),
        ])
        if (cancelled) return
        setAllPresets(presetsRes.data.presets)
        setMasks(masksRes.data.masks)
      } catch (err) {
        if (cancelled) return
        setPresetsError((err as Error).message ?? 'Failed to load presets')
      } finally {
        if (!cancelled) setPresetsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (selectedPdfProductId) setActiveProductId(selectedPdfProductId)
  }, [selectedPdfProductId])

  // When the active product changes (or presets finish loading), default
  // to the product's `default` preset and seed the editor with it.
  useEffect(() => {
    if (!activeProductId || allPresets.length === 0) return
    const productPresets = allPresets.filter(
      (p) => p.productId === activeProductId,
    )
    const def =
      productPresets.find((p) => p.isDefault) ??
      productPresets.find((p) => variantFromPresetId(p) === DEFAULT_VARIANT) ??
      productPresets[0]
    if (!def) return
    setPickedVariant(variantFromPresetId(def))
    setPresetName(def.name)
    setOperations(
      stripTrailingFlatten(def.operations.map((op) => ({ ...(op as OpRow) }))),
    )
  }, [activeProductId, allPresets])

  const productPresets = useMemo(
    () => allPresets.filter((p) => p.productId === activeProductId),
    [allPresets, activeProductId],
  )

  // Items shape that the existing MDL Dropdown component expects.
  const presetDropdownItems = useMemo(
    () =>
      productPresets.map((p) => {
        const variant = variantFromPresetId(p)
        return {
          id: variant,
          name: `${p.isDefault ? '★ ' : ''}${p.name}  (${variant})`,
        }
      }),
    [productPresets],
  )

  const addOpDropdownItems = useMemo(
    () => OPERATION_DEFS.map((d) => ({ id: d.type, name: d.label })),
    [],
  )

  // Flat list of distinct products across all presets. Used by the product
  // dropdown so manual uploads (no auto-resolved productId) can still pick
  // a preset family. Display name strips any " — <variant>" trailer from
  // the preset name to show just the product.
  const productDropdownItems = useMemo(() => {
    const seen = new Map<string, string>()
    for (const p of allPresets) {
      if (seen.has(p.productId)) continue
      const trimmed = p.name.split(' — ')[0]?.trim() || p.productId
      seen.set(p.productId, trimmed)
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }))
  }, [allPresets])

  const handlePresetChange = (variant: string) => {
    if (!variant) return
    const next = productPresets.find((p) => variantFromPresetId(p) === variant)
    if (!next) return
    setPickedVariant(variant)
    setPresetName(next.name)
    setOperations(
      stripTrailingFlatten(next.operations.map((op) => ({ ...(op as OpRow) }))),
    )
    setActionError(null)
  }

  const handleAddOp = (type: string) => {
    if (!type) return
    setOperations((prev) => [...prev, blankOperation(type) as OpRow])
  }

  const handleRemoveOp = (idx: number) => {
    setOperations((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleFieldChange = (
    idx: number,
    fieldName: string,
    value: unknown,
  ) => {
    setOperations((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], [fieldName]: value }
      return next
    })
  }

  const refreshPresets = async () => {
    const presetsRes = await getMessengerPressPresets()
    setAllPresets(presetsRes.data.presets)
  }

  const buildPresetPayload = (
    variant: string,
    isDefault: boolean,
  ): MessengerPressPreset => ({
    id: `${activeProductId}__${variant}`,
    name: presetName.trim() || `${variant} preset`,
    productId: activeProductId,
    isDefault,
    // Always append the terminal flatten step before persisting — the
    // client never adds it manually but the BE pipeline requires it.
    operations: appendFlatten(
      operations,
    ) as unknown as MessengerPressOperation[],
  })

  const handleSave = async () => {
    if (!activeProductId) return
    try {
      setSaving(true)
      setActionError(null)
      await putMessengerPressPreset(
        activeProductId,
        pickedVariant,
        buildPresetPayload(pickedVariant, pickedVariant === DEFAULT_VARIANT),
      )
      await refreshPresets()
      setToast({ text: `Saved “${presetName}”.`, icon: 'Check' })
    } catch (err) {
      const msg = (err as Error).message ?? 'Failed to save preset'
      setActionError(msg)
      setToast({ text: msg, icon: 'Error' })
    } finally {
      setSaving(false)
    }
  }

  const sanitizeVariant = (raw: string): string =>
    raw
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')

  const handleSaveAsConfirm = async () => {
    if (!activeProductId) return
    const variant = sanitizeVariant(saveAsName)
    if (!variant || variant === DEFAULT_VARIANT) {
      setActionError(
        variant === DEFAULT_VARIANT
          ? 'Use Save (not Save As) to overwrite the default preset.'
          : 'Variant name cannot be empty.',
      )
      return
    }
    try {
      setSaving(true)
      setActionError(null)
      await putMessengerPressPreset(
        activeProductId,
        variant,
        buildPresetPayload(variant, false),
      )
      await refreshPresets()
      setPickedVariant(variant)
      setSaveAsOpen(false)
      setSaveAsName('')
      setToast({ text: `Saved as new variant “${variant}”.`, icon: 'Check' })
    } catch (err) {
      const msg = (err as Error).message ?? 'Failed to save preset'
      setActionError(msg)
      setToast({ text: msg, icon: 'Error' })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (pickedVariant === DEFAULT_VARIANT) {
      setActionError(
        'Cannot delete the default preset. Promote another variant first.',
      )
      return
    }
    try {
      setSaving(true)
      setActionError(null)
      await deleteMessengerPressPreset(activeProductId, pickedVariant)
      await refreshPresets()
      setPickedVariant(DEFAULT_VARIANT)
      setConfirmDelete(false)
      setToast({ text: 'Preset deleted.', icon: 'Check' })
    } catch (err) {
      const msg = (err as Error).message ?? 'Failed to delete preset'
      setActionError(msg)
      setToast({ text: msg, icon: 'Error' })
    } finally {
      setSaving(false)
    }
  }

  const confirmPromoteAction = async () => {
    setConfirmPromote(false)
    try {
      setSaving(true)
      setActionError(null)
      await promoteMessengerPressPresetToDefault(activeProductId, pickedVariant)
      await refreshPresets()
      setToast({
        text: `Promoted “${pickedVariant}” to default.`,
        icon: 'Check',
      })
    } catch (err) {
      const msg = (err as Error).message ?? 'Failed to promote preset'
      setActionError(msg)
      setToast({ text: msg, icon: 'Error' })
    } finally {
      setSaving(false)
    }
  }

  // A source is either a picked DB row (selectedPdfId without the manual
  // prefix) or a manual upload (manual-upload: id + selectedPdfUrl). Both
  // gate canSubmit but map to different payload fields.
  const sourceReady = isManualUpload(selectedPdfId)
    ? !!selectedPdfUrl
    : !!selectedPdfId

  const canSubmit =
    !submitting && sourceReady && operations.length > 0 && !!activeProductId

  const handleSubmitJob = async () => {
    if (!sourceReady) {
      setActionError('Pick or upload a source PDF on the left first.')
      return
    }
    if (operations.length === 0) {
      setActionError('Recipe has no operations.')
      return
    }
    try {
      setSubmitting(true)
      setActionError(null)
      const ops = appendFlatten(
        operations,
      ) as unknown as MessengerPressOperation[]
      const themeIdExtra = themeIdOverride.trim()
        ? { themeId: themeIdOverride.trim() }
        : {}
      const payload: CreateMessengerPressPayload = isManualUpload(selectedPdfId)
        ? {
            mode: 'byOperations',
            operations: ops,
            sourcePdfUrl: selectedPdfUrl,
            ...themeIdExtra,
          }
        : {
            mode: 'byOperations',
            operations: ops,
            sourcePdfId: selectedPdfId,
            ...themeIdExtra,
          }
      const response = await createMessengerPressJob(payload)
      onJobCreated?.(response.data)
      setToast({ text: 'Job enqueued.', icon: 'Check' })
    } catch (err) {
      const msg = (err as Error).message ?? 'Failed to enqueue job'
      setActionError(msg)
      setToast({ text: msg, icon: 'Error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Panel data-testid={TID_RECIPE_BUILDER}>
      {/* Single scrollable area — everything scrolls together EXCEPT
          the Run button at the bottom, which stays pinned. */}
      <ScrollingArea>
        <Flex column>
          <Text size="18px" weight="700">
            Recipe Builder
          </Text>
          <Text size="13px" color="gray2" margin="4px 0 0 0">
            Pick a preset, edit operations, save back to S3, then run.
          </Text>
        </Flex>

        <Flex column margin="16px 0 0 0">
          <Text size="12px" weight="600" color="gray2" margin="0 0 6px 0">
            PRODUCT
          </Text>
          <Dropdown
            data-testid={TID_PRODUCT_SELECT}
            full
            color="inherit"
            selected={[activeProductId, setActiveProductId]}
            placeholder="Select product"
            items={productDropdownItems}
            valueKey="id"
            textKey="name"
          />
        </Flex>

        <Flex column margin="16px 0 0 0">
          <Text size="12px" weight="600" color="gray2" margin="0 0 6px 0">
            PRESET
          </Text>
          {presetsLoading && (
            <Text size="12px" color="gray2">
              Loading presets…
            </Text>
          )}
          {presetsError && (
            <Text size="12px" color="error">
              Error: {presetsError}
            </Text>
          )}
          {!presetsLoading && !presetsError && (
            <Dropdown
              data-testid={TID_PRESET_SELECT}
              full
              color="inherit"
              selected={[pickedVariant, handlePresetChange]}
              placeholder={
                productPresets.length === 0
                  ? '— No presets for this product —'
                  : 'Select preset'
              }
              items={presetDropdownItems}
              valueKey="id"
              textKey="name"
              disabled={productPresets.length === 0}
            />
          )}
        </Flex>

        <Flex column margin="16px 0 0 0">
          <Input
            full
            label="Display name"
            value={[presetName, setPresetName]}
            placeholder="e.g. Standard Card — 8-up Default"
          />
        </Flex>

        <Flex column margin="20px 0 0 0">
          <Text size="12px" weight="600" color="gray2" margin="0 0 4px 0">
            OPERATIONS
          </Text>
          <Text size="11px" color="gray2" margin="0 0 8px 0">
            A final Ghostscript flatten step is appended automatically — you
            don't need to add it.
          </Text>
          <OperationsList data-testid={TID_OPERATIONS_LIST}>
            {operations.length === 0 && (
              <EmptyOps>
                <Text size="13px" color="gray2">
                  No operations yet. Add one with the dropdown below.
                </Text>
              </EmptyOps>
            )}
            {operations.map((op, idx) => {
              const def = operationDefByType(op.type)
              return (
                <OpCard
                  key={idx}
                  data-testid={`${TID_OPERATION_STEP_PREFIX}${idx + 1}`}
                >
                  <Flex justify="space-between" align="center">
                    <Text size="14px" weight="700">
                      {def?.label ?? op.type}{' '}
                      <Text as="span" size="12px" color="gray2" weight="500">
                        (step {idx + 1} of {operations.length})
                      </Text>
                    </Text>
                    <IconButton
                      title="Remove this step"
                      icon="trashIcon"
                      width="28px"
                      height="28px"
                      onClick={() => handleRemoveOp(idx)}
                    />
                  </Flex>
                  {def?.description && (
                    <Text size="12px" color="gray2" margin="4px 0 10px 0">
                      {def.description}
                    </Text>
                  )}
                  {def && def.fields.length > 0 && (
                    <FieldsGrid>
                      {def.fields.map((field) => (
                        <FieldGroup key={field.name}>
                          <FieldRenderer
                            field={field}
                            value={op[field.name]}
                            masks={masks}
                            onChange={(v) =>
                              handleFieldChange(idx, field.name, v)
                            }
                          />
                          {field.helpText && (
                            <Text size="10px" color="gray3" margin="2px 0 0 0">
                              {field.helpText}
                            </Text>
                          )}
                        </FieldGroup>
                      ))}
                    </FieldsGrid>
                  )}
                </OpCard>
              )
            })}
          </OperationsList>

          <Flex margin="10px 0 0 0">
            <Dropdown
              data-testid={TID_ADD_OP_SELECT}
              full
              color="inherit"
              selected={['', (type: string) => handleAddOp(type)]}
              placeholder="+ Add operation…"
              items={addOpDropdownItems}
              valueKey="id"
              textKey="name"
            />
          </Flex>
        </Flex>

        <Flex margin="20px 0 0 0" gap="8px" wrap="wrap">
          <Button
            data-testid={TID_SAVE_BUTTON}
            primary
            disabled={saving || !activeProductId}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save preset'}
          </Button>
          <Button
            data-testid={TID_SAVE_AS_BUTTON}
            disabled={saving || !activeProductId}
            onClick={() => setSaveAsOpen(true)}
          >
            Save as new…
          </Button>
          <Button
            data-testid={TID_PROMOTE_BUTTON}
            disabled={saving || pickedVariant === DEFAULT_VARIANT}
            onClick={() => setConfirmPromote(true)}
          >
            Make default
          </Button>
          <Button
            data-testid={TID_DELETE_BUTTON}
            destructive
            disabled={saving || pickedVariant === DEFAULT_VARIANT}
            onClick={() => setConfirmDelete(true)}
          >
            Delete
          </Button>
        </Flex>

        <Flex column margin="20px 0 0 0">
          <Text size="12px" weight="600" color="gray2" margin="0 0 6px 0">
            THEME ID OVERRIDE (optional)
          </Text>
          <Input
            full
            value={[themeIdOverride, setThemeIdOverride]}
            placeholder="uuid — for testing theme-gated ops on manual uploads"
          />
          <Text size="11px" color="gray2" margin="4px 0 0 0">
            Queue-driven jobs auto-resolve this from the source OrderProduct.
            Set it manually to test prependPdf / removePages theme gates on an
            uploaded file.
          </Text>
        </Flex>

        <Flex column margin="20px 0 0 0">
          <Text size="12px" weight="600" color="gray2" margin="0 0 4px 0">
            SOURCE PDF
          </Text>
          {sourceReady ? (
            <Text size="12px" color="gray2">
              {isManualUpload(selectedPdfId) ? 'Uploaded' : 'Selected'}:{' '}
              <Mono>{selectedPdfLabel ?? selectedPdfId}</Mono>
            </Text>
          ) : (
            <Text size="12px" color="gray2">
              None selected. Pick or upload a PDF in the left panel.
            </Text>
          )}
        </Flex>

        {actionError && (
          <Text size="12px" color="error" margin="12px 0 0 0">
            {actionError}
          </Text>
        )}
      </ScrollingArea>

      {/* Sticky bottom — ONLY the Run MessengerPress button stays
          pinned. Everything else (header, preset picker, display name,
          operations, CRUD buttons, source pdf indicator) scrolls above
          it. Keeps the Run action always reachable no matter how many
          operations the admin has added to the recipe. */}
      <StickyRunBar>
        <Button
          data-testid={TID_RUN_BUTTON}
          primary
          full
          center
          disabled={!canSubmit}
          onClick={handleSubmitJob}
        >
          {submitting ? 'Running…' : 'Run MessengerPress'}
        </Button>
      </StickyRunBar>

      {/* Promote-default confirmation modal */}
      <FullscreenModal show={[confirmPromote, setConfirmPromote]} hideClose>
        <Flex column padding="32px" width="500px" gap="16px">
          <Text size="20px" weight="700">
            Overwrite default preset?
          </Text>
          <Text size="14px" color="gray1">
            Promoting <strong>{pickedVariant}</strong> to default will{' '}
            <strong>overwrite</strong> the existing default preset for this
            product. The old default's contents will be lost unless you saved
            them as a separate variant first.
          </Text>
          <Flex justify="flex-end" margin="8px 0 0 0" gap="12px">
            <Button onClick={() => setConfirmPromote(false)}>Cancel</Button>
            <Button destructive onClick={confirmPromoteAction}>
              Yes, overwrite default
            </Button>
          </Flex>
        </Flex>
      </FullscreenModal>

      {/* Save as… modal */}
      <FullscreenModal show={[saveAsOpen, setSaveAsOpen]} hideClose>
        <Flex column padding="32px" width="500px" gap="16px">
          <Text size="20px" weight="700">
            Save as new variant
          </Text>
          <Text size="13px" color="gray1">
            Pick a short name for the new variant. Letters, digits, and dashes
            only — for example <Mono>8up-test</Mono> or <Mono>no-shift</Mono>.
            The display name will reuse what you have in the editor.
          </Text>
          <Input
            full
            label="Variant slug"
            value={[saveAsName, setSaveAsName]}
            placeholder="e.g. 8up-test"
          />
          <Flex justify="flex-end" margin="8px 0 0 0" gap="12px">
            <Button
              onClick={() => {
                setSaveAsOpen(false)
                setSaveAsName('')
              }}
            >
              Cancel
            </Button>
            <Button
              primary
              disabled={saving || !sanitizeVariant(saveAsName)}
              onClick={handleSaveAsConfirm}
            >
              {saving ? 'Saving…' : 'Save new variant'}
            </Button>
          </Flex>
        </Flex>
      </FullscreenModal>

      {/* Delete confirmation modal */}
      <FullscreenModal show={[confirmDelete, setConfirmDelete]} hideClose>
        <Flex column padding="32px" width="500px" gap="16px">
          <Text size="20px" weight="700">
            Delete preset variant?
          </Text>
          <Text size="14px" color="gray1">
            Delete the variant <strong>{pickedVariant}</strong>? This removes
            the file from S3 and cannot be undone.
          </Text>
          <Flex justify="flex-end" margin="8px 0 0 0" gap="12px">
            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button destructive onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </Flex>
        </Flex>
      </FullscreenModal>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// FieldRenderer — picks the right MDL input for each catalog field type.
// Numeric fields use a debounced text-input wrapper because the MDL Input
// component's `type="number"` mode does parseInt() which strips decimals;
// we need 2.5 / 4.25 / 0.125 to round-trip cleanly.
// ---------------------------------------------------------------------------

type FieldRendererProps = {
  field: FieldDef
  value: unknown
  masks: string[]
  onChange: (value: unknown) => void
}

const FieldRenderer = ({
  field,
  value,
  masks,
  onChange,
}: FieldRendererProps) => {
  if (field.kind === 'mask') {
    const items = masks.map((m) => ({ id: m, name: m }))
    return (
      <Dropdown
        full
        color="inherit"
        label={`${field.label}`}
        selected={[
          typeof value === 'string' ? value : '',
          (next: string) => onChange(next),
        ]}
        placeholder="Select mask file"
        items={items}
        valueKey="id"
        textKey="name"
      />
    )
  }
  if (field.kind === 'enum') {
    const items = (field.options ?? []).map((opt) => ({
      id: String(opt),
      name: String(opt),
    }))
    return (
      <Dropdown
        full
        color="inherit"
        label={field.label}
        selected={[
          value === undefined || value === null ? '' : String(value),
          (next: string) => {
            // Coerce numeric enum values back to numbers (e.g. rotate.degrees)
            const allOptionsAreNumbers = (field.options ?? []).every(
              (o) => typeof o === 'number' || /^\d+$/.test(String(o)),
            )
            if (allOptionsAreNumbers) {
              const n = parseInt(next, 10)
              onChange(Number.isFinite(n) ? n : next)
            } else {
              onChange(next)
            }
          },
        ]}
        placeholder={field.label}
        items={items}
        valueKey="id"
        textKey="name"
      />
    )
  }

  if (field.kind === 'boolean') {
    const items = [
      { id: 'false', name: 'No' },
      { id: 'true', name: 'Yes' },
    ]
    return (
      <Dropdown
        full
        color="inherit"
        label={field.label}
        selected={[
          value === true ? 'true' : 'false',
          (next: string) => onChange(next === 'true'),
        ]}
        placeholder={field.label}
        items={items}
        valueKey="id"
        textKey="name"
      />
    )
  }

  if (field.kind === 'integer-array') {
    const text = Array.isArray(value) ? value.join(', ') : ''
    return (
      <Input
        full
        label={field.label}
        value={[
          text,
          (raw: string) => {
            const parsed = raw
              .split(/[\s,]+/)
              .filter((s) => s.length > 0)
              .map((s) => parseInt(s, 10))
              .filter((n) => Number.isFinite(n))
            onChange(parsed)
          },
        ]}
        placeholder="0, 1, 2, 3"
      />
    )
  }

  if (field.kind === 'string-array') {
    const text = Array.isArray(value) ? value.join(', ') : ''
    return (
      <Input
        full
        label={field.label}
        value={[
          text,
          (raw: string) => {
            const parsed = raw
              .split(/[\s,]+/)
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
            onChange(parsed)
          },
        ]}
        placeholder="uuid-1, uuid-2"
      />
    )
  }

  // number / integer — see header comment for the parseInt workaround.
  return (
    <NumericField
      label={`${field.label}${field.unit ? ` (${field.unit})` : ''}`}
      isInteger={field.kind === 'integer'}
      value={value}
      onChange={onChange}
    />
  )
}

type NumericFieldProps = {
  label: string
  isInteger: boolean
  value: unknown
  onChange: (value: number) => void
}

const NumericField = ({
  label,
  isInteger,
  value,
  onChange,
}: NumericFieldProps) => {
  // Local string state so the user can type intermediate values like "2."
  // or "-" without us snapping back to a parsed number on every keystroke.
  // We push parsed numbers to the parent only when the input is a valid
  // finite number; the visible string always reflects what was typed.
  const initial =
    typeof value === 'number' || typeof value === 'string' ? String(value) : ''
  const [text, setText] = useState(initial)

  // Resync local string when the upstream value changes (e.g. preset
  // switch or "Add operation" inserts a new row with default zero).
  useEffect(() => {
    const next =
      typeof value === 'number' || typeof value === 'string'
        ? String(value)
        : ''
    setText((prev) => {
      // Avoid clobbering an in-progress edit if the parsed value matches.
      const parsed = isInteger ? parseInt(prev, 10) : parseFloat(prev)
      if (
        Number.isFinite(parsed) &&
        typeof value === 'number' &&
        parsed === value
      ) {
        return prev
      }
      return next
    })
  }, [value, isInteger])

  return (
    <Input
      full
      label={label}
      // Use type="text" — the MDL Input wrapper does parseInt for type="number"
      // which would silently destroy decimal inch values.
      value={[
        text,
        (raw: string) => {
          setText(raw)
          if (raw === '' || raw === '-' || raw === '.') return
          const parsed = isInteger ? parseInt(raw, 10) : parseFloat(raw)
          if (Number.isFinite(parsed)) onChange(parsed)
        },
      ]}
      // Hint to mobile / desktop browsers that this is numeric input.
      inputMode={isInteger ? 'numeric' : 'decimal'}
    />
  )
}

// ---------------------------------------------------------------------------
// Layout-only styled wrappers — kept minimal because the MDL Flex/Text/Button
// components own the visual language. These exist only for the panel
// background, the operations list scroll container, op card chrome, and
// the inline monospace text used to display ids.
// ---------------------------------------------------------------------------

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors('palePrimary')};
  border-radius: 12px;
  padding: 20px;
  /* Inherit the grid row's height envelope from MessengerPressPage's
     TopGrid. The Panel itself never scrolls — the ScrollingArea owns
     the scroll context so the StickyRunBar stays pinned at the
     bottom. */
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

const ScrollingArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Give the scroll thumb a bit of margin so it doesn't sit flush
     against the edge of the panel's rounded border. */
  padding-right: 4px;
  margin-right: -4px;
`

const StickyRunBar = styled.div`
  flex: 0 0 auto;
  padding-top: 16px;
  border-top: 1px solid ${colors('gray4')};
  margin-top: 12px;
`

const OperationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const OpCard = styled.div`
  background: ${colors('white')};
  color: ${colors('gray1')};
  border: 1px solid ${colors('gray4')};
  border-radius: 8px;
  padding: 12px 14px;
`

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`

const EmptyOps = styled.div`
  padding: 16px;
  border: 1px dashed ${colors('gray4')};
  border-radius: 8px;
  text-align: center;
`

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  font-size: 11px;
  background: ${colors('gray5')};
  padding: 1px 4px;
  border-radius: 3px;
`
